import React, { useMemo } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { Col, Headline, Layout, Row } from "@folio/stripes/components";
import { CHANNEL_LABEL_IDS } from "../../types/enforcement";
import { formatDateTime } from "../../utils/formatUtils";
import styles from "./EnforcementTimeline.module.css";
var CHANNEL_CSS = {
    EMAIL: styles.channelEmail,
    PHONE: styles.channelPhone,
    POSTAL: styles.channelPostal,
    DATA_BOX: styles.channelDataBox,
    NOTE: styles.channelNote,
};
var DELIVERY_STATUS_LABEL_IDS = {
    SENT: "ui-br-legaldeposits.enforcement.delivery.sent",
    DELIVERED: "ui-br-legaldeposits.enforcement.delivery.delivered",
    FAILED: "ui-br-legaldeposits.enforcement.delivery.failed",
    BOUNCED: "ui-br-legaldeposits.enforcement.delivery.bounced",
};
var EVENT_TYPE_CSS = {
    CREATION: styles.eventCreation,
    RECEIPT: styles.eventReceipt,
    OVERDUE: styles.eventOverdue,
    EXCLUSION: styles.eventExclusion,
    REINSTATEMENT: styles.eventReinstatement,
    DAMAGE_REPORT: styles.eventDamageReport,
    REQUEST: styles.eventRequest,
    REMINDER: styles.eventReminder,
    REMINDER_CLOSED: styles.eventReminderClosed,
    URGENCE: styles.eventUrgence,
    DAMAGED_CLAIM: styles.eventDamagedClaim,
    PRE_LITIGATION_NOTICE: styles.eventPreLitigation,
    MERGE: styles.eventMerge,
};
export var EnforcementTimeline = function (_a) {
    var events = _a.events, _b = _a.titleMessageId, titleMessageId = _b === void 0 ? "ui-br-legaldeposits.enforcement.timeline.title" : _b, _c = _a.emptyMessageId, emptyMessageId = _c === void 0 ? "ui-br-legaldeposits.enforcement.timeline.empty" : _c;
    var formatMessage = useIntl().formatMessage;
    var getBadge = function (event) {
        var _a, _b;
        if (event.eventType === "COMMUNICATION") {
            var cssClass_1 = (_a = CHANNEL_CSS[event.label]) !== null && _a !== void 0 ? _a : styles.channelNote;
            var labelId = CHANNEL_LABEL_IDS[event.label];
            return {
                cssClass: cssClass_1,
                text: labelId ? formatMessage({ id: labelId }) : event.label,
            };
        }
        var cssClass = (_b = EVENT_TYPE_CSS[event.eventType]) !== null && _b !== void 0 ? _b : styles.channelNote;
        var text = formatMessage({
            id: "ui-br-legaldeposits.enforcement.timeline.event.".concat(event.eventType),
        });
        return { cssClass: cssClass, text: text };
    };
    var renderedEvents = useMemo(function () {
        return events.map(function (event) {
            var _a;
            var badge = getBadge(event);
            return (React.createElement("div", { key: "".concat(event.eventType, "-").concat(event.id, "-").concat(event.date), className: styles.entry },
                React.createElement("div", { className: "".concat(styles.dot, " ").concat(badge.cssClass) }),
                React.createElement(Layout, { className: "full padding-bottom-gutter" },
                    React.createElement(Row, null,
                        React.createElement(Col, { xs: 12 },
                            React.createElement(Layout, { className: "display-flex flex-align-items-center" },
                                React.createElement("span", { className: "".concat(styles.badge, " ").concat(badge.cssClass) }, badge.text),
                                React.createElement("span", { className: styles.meta },
                                    formatDateTime(event.date),
                                    event.userType === "SYSTEM" ? (React.createElement(React.Fragment, null,
                                        " \u00B7 ",
                                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.enforcement.timeline.systemActor" }))) : event.userName ? (React.createElement(React.Fragment, null, " \u00B7 ".concat(event.userName),
                                        event.userStatus === "INACTIVE" && (React.createElement(React.Fragment, null,
                                            " ",
                                            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.enforcement.timeline.userInactiveSuffix" }))))) : null)))),
                    event.eventType === "COMMUNICATION" && event.deliveryStatus && (React.createElement(Row, null,
                        React.createElement(Col, { xs: 12 },
                            React.createElement("span", { className: styles.deliveryStatus }, formatMessage({
                                id: DELIVERY_STATUS_LABEL_IDS[event.deliveryStatus],
                            }))))),
                    event.subject && (React.createElement(Row, null,
                        React.createElement(Col, { xs: 12 },
                            React.createElement("strong", null, event.subject)))),
                    event.eventType === "COMMUNICATION" &&
                        event.recipientAddress && (React.createElement(Row, null,
                        React.createElement(Col, { xs: 12 },
                            React.createElement("span", { className: styles.meta },
                                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.enforcement.field.recipient" }),
                                ": ",
                                event.recipientAddress)))),
                    event.eventType === "MERGE" && (React.createElement("strong", null,
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.enforcement.timeline.mergedFrom", values: { name: (_a = event.label) !== null && _a !== void 0 ? _a : "" } }))),
                    event.eventType === "EXCLUSION" && event.label && (React.createElement(Row, null,
                        React.createElement(Col, { xs: 12 },
                            React.createElement("strong", null, formatMessage({
                                id: "ui-br-legaldeposits.enforcement.timeline.exclusionReason.".concat(event.label),
                            }))))),
                    event.eventType === "CREATION" &&
                        event.label &&
                        event.label !== "CREATED" && (React.createElement(Row, null,
                        React.createElement(Col, { xs: 12 },
                            React.createElement("strong", null, formatMessage({
                                id: "ui-br-legaldeposits.enforcement.timeline.extraCopy",
                            }, {
                                reason: formatMessage({
                                    id: "ui-br-legaldeposits.detail.volume.extraReason.".concat(event.label),
                                }),
                            }))))),
                    event.notes && (React.createElement(Row, null,
                        React.createElement(Col, { xs: 12 },
                            React.createElement("div", { className: styles.notes }, event.notes)))))));
        });
    }, [events, formatMessage]);
    return (React.createElement(Layout, { className: "padding-top-gutter" },
        titleMessageId && (React.createElement(Row, null,
            React.createElement(Col, null,
                React.createElement(Headline, { size: "small", margin: "none" },
                    React.createElement(FormattedMessage, { id: titleMessageId }))))),
        events.length === 0 && (React.createElement(Layout, { className: "padding-top-gutter" },
            React.createElement("em", null,
                React.createElement(FormattedMessage, { id: emptyMessageId })))),
        React.createElement("div", { className: styles.timeline }, renderedEvents)));
};
